<template>
  <div
    class="share-qr"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('shareQrTitle')"
    data-testid="audience-share-qr-modal"
    @keydown.esc="$emit('close')"
  >
    <button
      type="button"
      class="share-qr__backdrop"
      tabindex="-1"
      :aria-label="$t('closeAvatar')"
      data-testid="audience-share-qr-backdrop"
      @click="$emit('close')"
    />
    <div class="share-qr__card">
      <h2 class="share-qr__title">
        {{ $t('shareQrTitle') }}
      </h2>

      <div class="share-qr__image-frame">
        <img
          v-if="qrDataUrl"
          :src="qrDataUrl"
          :alt="$t('shareQrAlt')"
          class="share-qr__image"
          data-testid="audience-share-qr-image"
        >
      </div>

      <p v-if="accessCode" class="share-qr__code" data-testid="audience-share-qr-code">
        {{ $t('shareQrAccessCode') }}: <strong>{{ accessCode }}</strong>
      </p>

      <p class="share-qr__link" data-testid="audience-share-qr-link">
        {{ joinUrl }}
      </p>

      <p
        class="share-qr__status"
        role="status"
        aria-live="polite"
        data-testid="audience-share-qr-status"
      >
        {{ isCopied ? $t('shareQrLinkCopied') : '' }}
      </p>

      <div class="share-qr__actions">
        <button
          type="button"
          class="share-qr__action share-qr__action--primary"
          data-testid="audience-share-qr-copy-button"
          @click="copyLink"
        >
          {{ $t('shareQrCopyLink') }}
        </button>
        <button
          type="button"
          class="share-qr__action"
          :disabled="!qrDataUrl"
          data-testid="audience-share-qr-download-button"
          @click="downloadImage"
        >
          {{ $t('shareQrDownload') }}
        </button>
        <button
          v-if="canNativeShare"
          type="button"
          class="share-qr__action"
          data-testid="audience-share-qr-native-share-button"
          @click="nativeShare"
        >
          {{ $t('shareQrShare') }}
        </button>
      </div>

      <button
        ref="closeButton"
        type="button"
        class="share-qr__close"
        :aria-label="$t('closeAvatar')"
        data-testid="audience-share-qr-close-button"
        @click="$emit('close')"
      >
        <phosphor-icon name="x" />
      </button>
    </div>
  </div>
</template>

<script>
import QRCode from 'qrcode'
// Relative imports (not the `@/` alias): the pre-commit hook lints newly ADDED
// files with `import/no-unresolved` as an error and no alias resolver.
import util from '../util'
import PhosphorIcon from './icons/PhosphorIcon.vue'

const QR_PIXEL_WIDTH = 800
const COPIED_RESET_MS = 2000

export default {
  name: 'ShareQrModal',
  components: { PhosphorIcon },
  props: {
    joinUrl: { type: String, required: true },
    accessCode: { type: String, default: '' },
    presentationCode: { type: String, default: '' },
  },
  data() {
    return {
      qrDataUrl: '',
      isCopied: false,
      copiedTimer: null,
    }
  },
  computed: {
    canNativeShare() {
      return typeof navigator !== 'undefined' && typeof navigator.share === 'function'
    },
  },
  watch: {
    joinUrl: {
      immediate: true,
      handler: 'renderQr',
    },
  },
  mounted() {
    this.$refs.closeButton.focus()
  },
  beforeDestroy() {
    clearTimeout(this.copiedTimer)
  },
  methods: {
    async renderQr() {
      try {
        // Dark on white with a 4-module quiet zone: scanners need the contrast and margin regardless of theme.
        this.qrDataUrl = await QRCode.toDataURL(this.joinUrl, {
          errorCorrectionLevel: 'Q',
          width: QR_PIXEL_WIDTH,
          margin: 4,
          color: { dark: '#000000', light: '#FFFFFF' },
        })
      } catch (error) {
        this.qrDataUrl = ''
      }
    },
    async copyLink() {
      util.ga4Track('Share QR > Copy link', { presentation_code: this.presentationCode })
      try {
        await navigator.clipboard.writeText(this.joinUrl)
      } catch (error) {
        this.copyWithSelection()
      }
      this.isCopied = true
      clearTimeout(this.copiedTimer)
      this.copiedTimer = setTimeout(() => { this.isCopied = false }, COPIED_RESET_MS)
    },
    copyWithSelection() {
      const input = document.createElement('input')
      input.value = this.joinUrl
      document.body.appendChild(input)
      input.select()
      document.execCommand('copy')
      document.body.removeChild(input)
    },
    downloadImage() {
      util.ga4Track('Share QR > Download', { presentation_code: this.presentationCode })
      const anchor = document.createElement('a')
      anchor.href = this.qrDataUrl
      anchor.download = `ahaslides-${this.accessCode || 'join'}-qr.png`
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
    },
    async nativeShare() {
      util.ga4Track('Share QR > Native share', { presentation_code: this.presentationCode })
      try {
        await navigator.share({ title: this.$t('shareQrTitle'), url: this.joinUrl })
      } catch (error) {
        // AbortError when the user dismisses the share sheet is not a failure.
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.share-qr {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  box-sizing: border-box;

  &__backdrop {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    padding: 0;
    border: none;
    // theme('colors.secondary.100') at 35%
    background: rgba(26, 26, 26, 0.35);
    cursor: pointer;
  }

  &__card {
    position: relative;
    width: 100%;
    max-width: 360px;
    max-height: 100%;
    overflow-y: auto;
    box-sizing: border-box;
    padding: 24px 16px 16px;
    border-radius: 16px;
    background: theme('colors.base.0');
    color: theme('colors.secondary.100');
    text-align: center;
  }

  &__title {
    margin: 0 32px 16px;
    font-size: 1.125rem;
    font-weight: 700;
    line-height: 1.3;
    overflow-wrap: anywhere;
  }

  &__image-frame {
    width: 100%;
    max-width: 240px;
    aspect-ratio: 1 / 1;
    margin: 0 auto 12px;
    box-sizing: border-box;
    overflow: hidden;
    background: #fff;
    border: 1px solid theme('colors.secondary.20');
    border-radius: 8px;
  }

  &__image {
    display: block;
    width: 100%;
    height: 100%;
  }

  &__code {
    margin: 0 0 4px;
    font-size: 0.9375rem;
    overflow-wrap: anywhere;
  }

  &__link {
    margin: 0;
    font-size: 0.8125rem;
    color: theme('colors.secondary.50');
    overflow-wrap: anywhere;
  }

  &__status {
    min-height: 1.25rem;
    margin: 8px 0;
    font-size: 0.8125rem;
    color: theme('colors.primary.90');
  }

  &__actions {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__action {
    width: 100%;
    padding: 12px;
    border: 1px solid theme('colors.secondary.20');
    border-radius: 8px;
    background: theme('colors.base.0');
    color: theme('colors.secondary.100');
    font-size: 0.9375rem;
    font-weight: 600;
    cursor: pointer;

    &:hover {
      background: theme('colors.secondary.15');
    }

    &:disabled {
      color: theme('colors.secondary.40');
      cursor: not-allowed;
    }

    &--primary {
      border-color: transparent;
      background: theme('colors.primary.90');
      color: theme('colors.base.0');

      &:hover {
        background: theme('colors.primary.90');
        opacity: 0.9;
      }
    }
  }

  &__close {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: theme('colors.secondary.100');
    cursor: pointer;

    &:hover {
      background: theme('colors.secondary.15');
    }
  }
}
</style>
